import React, { useState, useEffect, useCallback, useRef } from "react";
import { Plus, CheckCircle2, Printer, Info, X, Search, Save, RotateCcw, Trash2, MapPin, Clock, Truck, Layers, FilePlus2, Edit3, AlertTriangle, ExternalLink } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { EquipmentModal } from "../components/EquipmentModal";
import { ReceivingLineItem } from "./receiving/ReceivingLineItem";
import { Collapsible } from "../components/Collapsible";
import { EndUserModal } from "../components/EndUserModal";
import { ProgressDots } from "../components/ProgressDots";
import { SUBCON_RETURN_REASONS } from "../components/ReturnToSubconModal";
import { SubcontractorTypeahead } from "../components/SubcontractorTypeahead";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const SUBCON_REASONS = SUBCON_RETURN_REASONS;

const _newUid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `it-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`);

const blankItem = () => ({
  _uid: _newUid(),
  description: "", manufacturer: "", part_number: "", serial_number: "", asset_number: "",
  issue_number: "", location: "Toolstore", cal_method: "traceable", group_item: false, group_number: "",
  discipline: "Dimensional", technician_id: "", calibration_period_days: 365, quantity: 1,
  receiving_condition: "In box/case/pouch", remarks: "", notes: "",
  priority: "normal",
  send_to_subcontractor: false, subcontractor_id: "", subcontractor_name: "",
  subcontractor_reason: "", delivery_method: "",
  last_subcontractor_name: null, last_subcontractor_id: null,
  matched_equipment_id: null, matched_meta: null, cross_customer_warning: null,
  unserviceable_flag: null,
});

export default function Receiving() {
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [custQ, setCustQ] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [techs, setTechs] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [receivedBy, setReceivedBy] = useState("");
  const [dn, setDn] = useState("");
  const [internalRef, setInternalRef] = useState("");
  const [notesAdmin, setNotesAdmin] = useState("");
  const [endUser, setEndUser] = useState(false);
  const [endUserName, setEndUserName] = useState("");
  const [endUserEmail, setEndUserEmail] = useState("");
  const [endUserPhone, setEndUserPhone] = useState("");
  const [endUserContactId, setEndUserContactId] = useState(null); // tracks the linked contact when picked from dropdown
  // Structured end-user certificate address fields
  const [endUserCompany, setEndUserCompany] = useState("");
  const [endUserAddr1, setEndUserAddr1] = useState("");
  const [endUserAddr2, setEndUserAddr2] = useState("");
  const [endUserCity, setEndUserCity] = useState("");
  const [endUserProvince, setEndUserProvince] = useState("");
  const [endUserCountry, setEndUserCountry] = useState("");
  const [endUserPostal, setEndUserPostal] = useState("");
  const [endUserAddrUnknown, setEndUserAddrUnknown] = useState(false);
  const [endUserAddrUnknownReason, setEndUserAddrUnknownReason] = useState("");
  const [contactsOpen, setContactsOpen] = useState(false);
  const [primaryContact, setPrimaryContact] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [dateReceived, setDateReceived] = useState(new Date().toISOString().slice(0, 10));
  const [calType, setCalType] = useState("traceable");
  const [quoteRef, setQuoteRef] = useState("");
  const [items, setItems] = useState([blankItem()]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  // ---- Draft autosave state (multiple drafts per user) ----
  const [drafts, setDrafts] = useState([]);          // list summaries
  const [draftsOpen, setDraftsOpen] = useState(false); // drafts panel
  const [currentDraftId, setCurrentDraftId] = useState(null);
  // SPT-PTE-2026-0019: idempotency key for THIS submission attempt — prefers
  // the draft's own (server-persisted, stable-across-reopens) id once one
  // exists, falling back to a client-generated UUID before that. Sent as
  // client_request_id on POST /jobs so a double-click, a slow-request retry,
  // or reopening this same not-yet-cleared draft can never create a second
  // job — the backend returns the already-created job instead.
  const [clientRequestId, setClientRequestId] = useState(() => _newUid());
  const [draftChecked, setDraftChecked] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [confirmDelDraft, setConfirmDelDraft] = useState(null); // draft summary pending delete
  // ---- Manager/admin: view EVERYONE's saved drafts (SPT-PTE-2026-0051) ----
  const isManager = ["manager", "admin"].includes(user?.role);
  const [allDraftsOpen, setAllDraftsOpen] = useState(false);
  const [allDrafts, setAllDrafts] = useState([]);
  const [allDraftsQ, setAllDraftsQ] = useState("");
  const [allDraftsBusy, setAllDraftsBusy] = useState(false);
  const [viewingDraft, setViewingDraft] = useState(null); // {summary, payload} | null
  const [subcontractors, setSubcontractors] = useState([]);
  // Whole-job subcontractor routing
  const [jobToSubcon, setJobToSubcon] = useState(false);
  const [jobSubconId, setJobSubconId] = useState("");
  const [jobSubconName, setJobSubconName] = useState("");
  const [jobSubconReason, setJobSubconReason] = useState("");
  const [jobSubconDelivery, setJobSubconDelivery] = useState("");
  // End-user modal pop-up
  const [endUserModalOpen, setEndUserModalOpen] = useState(false);
  const saveTimer = useRef(null);
  const creatingDraft = useRef(false);
  const allDraftsSearchTimer = useRef(null);

  useEffect(() => { api.get("/employees", { params: { role: "technician" } }).then((r) => setTechs(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/employees", { params: { can_receive: true } }).then((r) => {
      const list = r.data || [];
      setReceivers(list);
      // default to the logged-in user if they are an allowed receiver
      setReceivedBy((cur) => cur || (list.find((e) => e.id === user?.id)?.id || ""));
    }).catch(() => {});
  }, [user?.id]);
  useEffect(() => { api.get("/subcontractors", { params: { limit: 300, active_only: true, sort_by: "name", sort_dir: "asc" } }).then((r) => setSubcontractors(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (custQ.length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => api.get("/customers", { params: { search: custQ, limit: 200 } }).then((r) => setCustResults(r.data.items)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [custQ]);
  const [dnDupMatches, setDnDupMatches] = useState([]);
  useEffect(() => {
    if (dn.trim().length < 2) { setDnDupMatches([]); return; }
    const t = setTimeout(() => {
      api.get("/jobs/duplicate-check", { params: { delivery_note_number: dn.trim() } })
        .then((r) => setDnDupMatches(r.data.matches || [])).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [dn]);

  // when a customer is picked, fetch the enriched detail so we know the primary contact
  useEffect(() => {
    if (!customer?.id) { setPrimaryContact(null); setContacts([]); return; }
    api.get(`/customers/${customer.id}`).then((r) => {
      const p = r.data?.primary_contact || null;
      setPrimaryContact(p);
      setContacts(r.data?.contacts || []);
      // merge enriched fields back onto the selected customer (phone, address, etc.)
      setCustomer((prev) => prev && prev.id === r.data.id ? { ...prev, ...r.data } : prev);
      // auto-fill end-user fields from primary contact when present and not yet set by user
      if (p) {
        setEndUserName((cur) => cur || p.name || "");
        setEndUserEmail((cur) => cur || p.email || "");
        setEndUserPhone((cur) => cur || p.phone || "");
        setEndUserContactId((cur) => cur || p.id || null);
      } else {
        // fallback to legacy contact_person on the customer record
        setEndUserName((cur) => cur || r.data.contact_person || "");
        setEndUserEmail((cur) => cur || r.data.email || "");
        setEndUserPhone((cur) => cur || r.data.phone || "");
      }
    }).catch(() => { setPrimaryContact(null); setContacts([]); });
  }, [customer?.id]); // eslint-disable-line

  // Last issue numbers PTE has allocated for this customer — helps Receiving
  // know the next one to allocate for un-serialised equipment (e.g. ISW078 → ISW079).
  const [lastIssueNumbers, setLastIssueNumbers] = useState([]);
  useEffect(() => {
    if (!customer?.id) { setLastIssueNumbers([]); return; }
    api.get(`/customers/${customer.id}/last-issue-numbers`)
      .then((r) => setLastIssueNumbers(r.data?.items || []))
      .catch(() => setLastIssueNumbers([]));
  }, [customer?.id]);

  const setItem = (i, patch) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setItems([...items, blankItem()]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  // ---- Draft: collect / restore the full form state ----
  const collectDraft = useCallback(() => ({
    customer, dn, internalRef, notesAdmin, dateReceived, calType, quoteRef, receivedBy,
    endUser, endUserName, endUserEmail, endUserPhone, endUserContactId,
    endUserCompany, endUserAddr1, endUserAddr2, endUserCity, endUserProvince,
    endUserCountry, endUserPostal, endUserAddrUnknown, endUserAddrUnknownReason,
    jobToSubcon, jobSubconId, jobSubconName, jobSubconReason, jobSubconDelivery,
    items,
  }), [customer, dn, internalRef, notesAdmin, dateReceived, calType, quoteRef, receivedBy,
       endUser, endUserName, endUserEmail, endUserPhone, endUserContactId,
       endUserCompany, endUserAddr1, endUserAddr2, endUserCity, endUserProvince,
       endUserCountry, endUserPostal, endUserAddrUnknown, endUserAddrUnknownReason,
       jobToSubcon, jobSubconId, jobSubconName, jobSubconReason, jobSubconDelivery, items]);

  const restoreDraft = (p) => {
    if (!p) return;
    setCustomer(p.customer || null);
    setDn(p.dn || ""); setInternalRef(p.internalRef || ""); setNotesAdmin(p.notesAdmin || "");
    setDateReceived(p.dateReceived || new Date().toISOString().slice(0, 10));
    setCalType(p.calType || "traceable"); setQuoteRef(p.quoteRef || "");
    if (p.receivedBy !== undefined) setReceivedBy(p.receivedBy || "");
    setEndUser(!!p.endUser); setEndUserName(p.endUserName || ""); setEndUserEmail(p.endUserEmail || "");
    setEndUserPhone(p.endUserPhone || ""); setEndUserContactId(p.endUserContactId || null);
    setEndUserCompany(p.endUserCompany || ""); setEndUserAddr1(p.endUserAddr1 || "");
    setEndUserAddr2(p.endUserAddr2 || ""); setEndUserCity(p.endUserCity || "");
    setEndUserProvince(p.endUserProvince || ""); setEndUserCountry(p.endUserCountry || "");
    setEndUserPostal(p.endUserPostal || ""); setEndUserAddrUnknown(!!p.endUserAddrUnknown);
    setEndUserAddrUnknownReason(p.endUserAddrUnknownReason || "");
    setJobToSubcon(!!p.jobToSubcon); setJobSubconId(p.jobSubconId || ""); setJobSubconName(p.jobSubconName || "");
    setJobSubconReason(p.jobSubconReason || ""); setJobSubconDelivery(p.jobSubconDelivery || "");
    setItems(Array.isArray(p.items) && p.items.length ? p.items.map((it) => (it && it._uid ? it : { ...it, _uid: _newUid() })) : [blankItem()]);
  };

  const draftHasContent = (p) =>
    !!(p && (p.customer || (p.dn || "").trim() ||
      (Array.isArray(p.items) && p.items.some((it) => (it.description || "").trim()))));

  const loadDrafts = useCallback(() => api.get("/receiving/drafts")
    .then((r) => setDrafts(r.data?.drafts || [])).catch(() => {}), []);

  // On mount: fetch the list of saved drafts (do not auto-load any) — unless
  // a specific draft was requested via ?resumeDraft=<id> (SPT-PTE-2026-0044,
  // used by the Calibration Due page's "open pre-filled job" link, which
  // opens this page in a new tab already carrying that query param).
  useEffect(() => {
    loadDrafts().finally(() => setDraftChecked(true));
    const wantDraft = new URLSearchParams(window.location.search).get("resumeDraft");
    if (wantDraft) {
      resumeDraft(wantDraft);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [loadDrafts]); // eslint-disable-line

  // Background autosave (debounced) into the current draft (creating one on
  // first content). Paused after submission and when the form is empty.
  useEffect(() => {
    if (!draftChecked || created) return;
    const payload = collectDraft();
    if (!draftHasContent(payload)) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        if (currentDraftId) {
          const r = await api.put(`/receiving/drafts/${currentDraftId}`, { payload });
          setDraftSavedAt(r.data?.saved_at || new Date().toISOString());
        } else if (!creatingDraft.current) {
          creatingDraft.current = true;
          const r = await api.post("/receiving/drafts", { payload });
          setCurrentDraftId(r.data?.id);
          setDraftSavedAt(r.data?.saved_at || new Date().toISOString());
          creatingDraft.current = false;
          loadDrafts();
        }
      } catch { creatingDraft.current = false; }
    }, 1500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [collectDraft, draftChecked, created, currentDraftId, loadDrafts]);

  const resumeDraft = async (id) => {
    try {
      const r = await api.get(`/receiving/drafts/${id}`);
      restoreDraft(r.data?.draft?.payload);
      setCurrentDraftId(id);
      setDraftsOpen(false);
      toast.success("Draft resumed");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteDraft = async (summary) => {
    try {
      await api.delete(`/receiving/drafts/${summary.id}`);
      if (summary.id === currentDraftId) { setCurrentDraftId(null); setDraftSavedAt(null); }
      setConfirmDelDraft(null);
      await loadDrafts();
      toast.success("Draft deleted");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const startNewDraft = () => {
    // Current work is already autosaved to its own draft — just clear the form.
    reset();
    setCurrentDraftId(null);
    setDraftsOpen(false);
    loadDrafts();
    toast.message("Started a fresh capture — your previous draft is saved under Drafts.");
  };

  const loadAllDrafts = useCallback((q) => {
    setAllDraftsBusy(true);
    api.get("/admin/receiving-drafts", { params: { search: q || undefined } })
      .then((r) => setAllDrafts(r.data?.drafts || []))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setAllDraftsBusy(false));
  }, []);
  const viewAllDraft = async (summary) => {
    try {
      const r = await api.get(`/admin/receiving-drafts/${summary.id}`);
      setViewingDraft({ summary, payload: r.data?.draft?.payload || {} });
    } catch (e) { toast.error(apiErr(e)); }
  };


  // SPT-PTE-2026-0045: identity fields that must never be silently overwritten
  // once already filled in — picking a match from ONE typeahead (e.g. Part
  // number) must not stomp another already-captured field (e.g. Serial
  // number). The field that was actually searched (`searchField`) is always
  // updated to the exact picked value; every other identity field only fills
  // in if it's still blank.
  const IDENTITY_FIELDS = ["description", "manufacturer", "part_number", "serial_number", "asset_number", "issue_number"];

  const applyMatch = (i, m, searchField) => {
    setItems((prev) => prev.map((it, idx) => {
      if (idx !== i) return it;
      const next = { ...it };
      IDENTITY_FIELDS.forEach((k) => {
        if (m[k] === undefined || m[k] === null || m[k] === "") return;
        if (k === searchField || !it[k]) next[k] = m[k];
      });
      next.discipline = m.discipline || it.discipline;
      next.calibration_period_days = m.calibration_period_days || it.calibration_period_days;
      next.matched_equipment_id = m.id;
      next.matched_meta = { last: m.last_calibration_date, due: m.calibration_due_date, history: m.calibration_history_count };
      next.last_subcontractor_name = m.last_subcontractor_name || null;
      next.last_subcontractor_id = m.last_subcontractor_id || null;
      next.in_service_warning = null;
      next.cross_customer_warning = null;
      return next;
    }));
    toast.success(`Loaded existing record · ${m.calibration_history_count || 0} past calibration(s)`);
    // Duplicate-in-service check — fire and forget, attach result to the line
    api.get(`/equipment/${m.id}/active-job-check`).then((r) => {
      if (r.data?.is_in_active_job) {
        setItem(i, { in_service_warning: r.data.active });
        toast.warning(`Heads-up: this equipment is already on DN ${r.data.active.delivery_note_number}`);
      }
    }).catch(() => {});
  };

  const create = async () => {
    if (!customer) return toast.error("Select a customer");
    if (!items.some((it) => it.description)) return toast.error("Add at least one equipment line with a description");
    const unservIdx = items.findIndex((it) => it.description && it.unserviceable_flag && !it.unserviceable_flag.acked);
    if (unservIdx >= 0) return toast.error(`Line ${unservIdx + 1}: confirm whether the unserviceable-flagged issue number can still be used`);
    if (jobToSubcon) {
      if (!jobSubconId) return toast.error("Select a subcontractor for the whole job, or turn off 'Send entire job to subcontractor'");
      if (!jobSubconReason) return toast.error("Select a Sub Con reason for the whole-job subcontractor routing");
    } else {
      const badLine = items.findIndex((it) => it.description && it.send_to_subcontractor && (!it.subcontractor_id || !it.subcontractor_reason));
      if (badLine >= 0) return toast.error(`Line ${badLine + 1}: choose a subcontractor and a Sub Con reason (or untick 'Send to subcontractor')`);
    }
    setBusy(true);
    try {
      // Persist End User contact info back to the customer record:
      // - If picked from dropdown AND email/phone changed → PATCH that contact
      // - If new name (no contact id) AND name is provided → POST new contact
      // Failures here are non-fatal; the job still gets created.
      const endName = (endUserName || "").trim();
      const endEmail = (endUserEmail || "").trim();
      const endPhone = (endUserPhone || "").trim();
      if (customer.id && endName) {
        try {
          if (endUserContactId) {
            const orig = contacts.find((c) => c.id === endUserContactId);
            const changed = orig && (
              (orig.email || "") !== endEmail ||
              (orig.phone || "") !== endPhone ||
              (orig.name || "") !== endName
            );
            if (changed) {
              await api.patch(`/customers/${customer.id}/contacts/${endUserContactId}`, {
                name: endName, email: endEmail, phone: endPhone,
              });
              toast.success("Contact details updated on the customer record");
            }
          } else {
            // Only persist if there's at least one piece of contact info beyond the name
            if (endEmail || endPhone) {
              const r = await api.post(`/customers/${customer.id}/contacts`, {
                name: endName, email: endEmail, phone: endPhone,
              });
              if (r.data?.created) {
                toast.success(`Saved "${endName}" as a new contact on this customer`);
              }
            }
          }
        } catch (e) {
          // swallow — don't block job creation on contact persistence
          console.warn("Contact persistence skipped:", apiErr(e));
        }
      }
      const payload = {
        customer_id: customer.id, delivery_note_number: dn || null, date_received: dateReceived,
        received_by: receivedBy || null,
        cal_type: calType, quote_or_estimate: quoteRef ? "estimate" : "quote", quote_number: quoteRef || null,
        end_user: endUser,
        end_user_name: endUserName || null,
        end_user_email: endUserEmail || null,
        end_user_phone: endUserPhone || null,
        end_user_company: endUserCompany || null,
        end_user_address_line_1: endUserAddr1 || null,
        end_user_address_line_2: endUserAddr2 || null,
        end_user_city: endUserCity || null,
        end_user_province: endUserProvince || null,
        end_user_country: endUserCountry || null,
        end_user_postal_code: endUserPostal || null,
        end_user_address_unknown: !!endUserAddrUnknown,
        end_user_address_unknown_reason: endUserAddrUnknownReason || null,
        contact_person: primaryContact?.name || customer.contact_person || null,
        notes_admin: notesAdmin || null,
        send_entire_job_to_subcontractor: !!jobToSubcon,
        job_subcontractor_id: jobToSubcon ? (jobSubconId || null) : null,
        job_subcontractor_name: jobToSubcon ? (jobSubconName || null) : null,
        job_subcontractor_reason: jobToSubcon ? (jobSubconReason || null) : null,
        job_subcontractor_delivery_method: jobToSubcon ? (jobSubconDelivery || null) : null,
        client_request_id: currentDraftId || clientRequestId,
        items: items.filter((it) => it.description).map((it) => {
          // eslint-disable-next-line no-unused-vars
          const { _uid, unserviceable_flag, ...rest } = it;
          return {
            ...rest,
            calibration_period_days: rest.calibration_period_days ? parseInt(rest.calibration_period_days) : null,
            quantity: parseInt(rest.quantity) || 1,
            technician_id: rest.technician_id || null,
            equipment_id: rest.matched_equipment_id || null,
            send_to_subcontractor: !!rest.send_to_subcontractor,
            subcontractor_id: rest.send_to_subcontractor ? (rest.subcontractor_id || null) : null,
            subcontractor_name: rest.send_to_subcontractor ? (rest.subcontractor_name || null) : null,
            subcontractor_reason: rest.send_to_subcontractor ? (rest.subcontractor_reason || null) : null,
            delivery_method: rest.send_to_subcontractor ? (rest.delivery_method || null) : null,
          };
        }),
      };
      const r = await api.post("/jobs", payload);
      toast.success("Job created & items assigned to the lab");
      if (currentDraftId) { await api.delete(`/receiving/drafts/${currentDraftId}`).catch(() => {}); setCurrentDraftId(null); }
      setDraftSavedAt(null);
      loadDrafts();
      setCreated(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadReceivingPdf = async () => {
    if (!created?.id) return;
    setPdfBusy(true);
    try {
      const res = await api.get(`/jobs/${created.id}/receiving-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Receiving_${(created.delivery_note_number || created.id).replace(/[\/ ]/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); } finally { setPdfBusy(false); }
  };


  const reset = () => { setCreated(null); setItems([blankItem()]); setDn(""); setInternalRef(""); setNotesAdmin(""); setQuoteRef(""); setCustomer(null); setCustQ(""); setEndUser(false); setEndUserName(""); setEndUserEmail(""); setEndUserPhone(""); setEndUserContactId(null); setEndUserCompany(""); setEndUserAddr1(""); setEndUserAddr2(""); setEndUserCity(""); setEndUserProvince(""); setEndUserCountry(""); setEndUserPostal(""); setEndUserAddrUnknown(false); setEndUserAddrUnknownReason(""); setContacts([]); setPrimaryContact(null); setDraftSavedAt(null); setJobToSubcon(false); setJobSubconId(""); setJobSubconName(""); setJobSubconReason(""); setJobSubconDelivery(""); setReceivedBy(receivers.find((e) => e.id === user?.id)?.id || ""); setClientRequestId(_newUid()); };

  // Client-side mirror of the backend certificate-address resolver.
  const certAddr = (() => {
    if (!customer) return null;
    const addrParts = [endUserAddr1, endUserAddr2, endUserCity, endUserProvince, endUserCountry, endUserPostal];
    const hasAddr = addrParts.some((v) => (v || "").trim());
    if (endUser && (hasAddr || endUserAddrUnknown || endUserCompany || endUserName)) {
      const unknown = endUserAddrUnknown && !hasAddr;
      return {
        source: "end_user",
        name: endUserCompany || endUserName || customer.customer_name,
        lines: unknown ? [] : [endUserAddr1, endUserAddr2,
          [endUserCity, endUserProvince].filter(Boolean).join(", "),
          [endUserCountry, endUserPostal].filter(Boolean).join(" · ")].filter(Boolean),
        unknown, reason: endUserAddrUnknownReason,
        contact: [endUserName, endUserEmail, endUserPhone].filter(Boolean).join(" · "),
      };
    }
    return {
      source: "customer",
      name: customer.customer_name,
      lines: [customer.address_line_1, customer.address_line_2,
        [customer.city, customer.province].filter(Boolean).join(", "),
        [customer.country, customer.postal_code].filter(Boolean).join(" · ")].filter(Boolean),
      contact: [primaryContact?.name || customer.contact_person, primaryContact?.email || customer.email].filter(Boolean).join(" · "),
    };
  })();

  // ---- Section summaries / missing-field flags for collapsible headers ----
  const jobDetailsSummary = [
    customer ? customer.customer_name : null,
    dn ? `DN ${dn}` : null,
    dateReceived,
    calType === "sanas" ? "SANAS" : "Traceable",
  ].filter(Boolean).join("  ·  ");
  const jobDetailsMissing = [
    !customer && "Customer",
    !dn && "Delivery note",
    !dateReceived && "Date received",
  ].filter(Boolean);

  const subconSummary = jobToSubcon
    ? `Whole job → ${jobSubconName || "(no subcontractor)"}${jobSubconReason ? ` · ${jobSubconReason}` : ""}`
    : "Per-line only";
  const subconMissing = jobToSubcon ? [
    !jobSubconId && "Subcontractor",
    !jobSubconReason && "Reason",
  ].filter(Boolean) : [];

  const endUserSummaryBits = [
    endUserName || endUserCompany || null,
    endUserEmail || null,
    endUserPhone || null,
    endUserAddrUnknown ? "Address not supplied" : (endUserCity || endUserAddr1 || null),
  ].filter(Boolean);
  const endUserSummary = endUserSummaryBits.length
    ? endUserSummaryBits.join("  ·  ")
    : "(not set — click Edit details)";
  const endUserMissing = endUser ? [
    !endUserName && !endUserCompany && "Name / company",
    !endUserAddrUnknown && !endUserAddr1 && !endUserCity && "Address",
    endUserAddrUnknown && !endUserAddrUnknownReason && "Reason",
  ].filter(Boolean) : [];

  const linesComplete = items.filter((it) => it.description).length;
  const linesIncomplete = items.length - linesComplete;
  const equipmentSummary = `${items.length} line${items.length !== 1 ? "s" : ""}${linesIncomplete > 0 ? ` · ${linesIncomplete} need details` : ""}`;
  const equipmentMissing = linesIncomplete > 0 ? [`${linesIncomplete} description${linesIncomplete > 1 ? "s" : ""}`] : [];

  const customerDetailsSummary = customer
    ? [primaryContact?.name || customer.contact_person, primaryContact?.email || customer.email, [customer.city, customer.country].filter(Boolean).join(", ")].filter(Boolean).join("  ·  ")
    : "";

  // ---- Per-section progress dots ----
  // Each entry: { key, label, missing[], skipped }.  `skipped` = section is
  // optional and the user hasn't engaged it (e.g. subcon not toggled on) — we
  // show a hollow dot rather than "complete green" so the visual doesn't lie.
  const progressSections = [
    { key: "job", label: "Job details", missing: jobDetailsMissing, skipped: false },
    { key: "subcon", label: "Subcontractor", missing: subconMissing, skipped: !jobToSubcon },
    // Customer-details section is informational — the "Customer" blocker is
    // already raised by Job details, so don't double-report it here.
    { key: "customer", label: "Customer details", missing: [], skipped: !customer },
    { key: "enduser", label: "End-user cert address", missing: endUserMissing, skipped: !endUser },
    { key: "lines", label: "Equipment lines", missing: equipmentMissing, skipped: false },
  ];
  const allComplete = progressSections.every((s) => s.missing.length === 0);
  const blockingMissing = progressSections.filter((s) => s.missing.length > 0)
    .flatMap((s) => s.missing.map((m) => `${s.label}: ${m}`));

  return (
    <>
      <PageHead crumb="Front Desk" title="Receiving / New Job"
        sub="Load a new job manually or from an RFQ. The same data flows straight to the lab — no re-capture."
        actions={
          <>
            <ProgressDots sections={progressSections} testid="rcv-progress" />
            <button className="btn ghost" onClick={() => { loadDrafts(); setDraftsOpen(true); }} data-testid="open-drafts-btn">
              <Layers size={15} /> Drafts{drafts.length ? ` (${drafts.length})` : ""}
            </button>
            {isManager && (
              <button className="btn ghost" onClick={() => { loadAllDrafts(allDraftsQ); setAllDraftsOpen(true); }} data-testid="open-all-drafts-btn">
                <Layers size={15} /> All staff drafts
              </button>
            )}
          </>
        } />

      {!created && drafts.length > 0 && (
        <div className="card pad" data-testid="drafts-banner"
          style={{ marginBottom: 14, borderLeft: "3px solid var(--gold-deep)", background: "var(--card-2)", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <Layers size={18} color="var(--gold-deep)" />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="t-strong" style={{ fontSize: 13.5 }}>You have {drafts.length} saved Receiving draft{drafts.length !== 1 ? "s" : ""}</div>
            <div className="t-mut" style={{ fontSize: 12 }}>
              Most recent: {drafts[0].label} · {drafts[0].item_count} line(s) · {drafts[0].updated_at ? new Date(drafts[0].updated_at).toLocaleString("en-ZA") : "recently"}
            </div>
          </div>
          <button className="btn gold" onClick={() => setDraftsOpen(true)} data-testid="review-drafts-btn">
            <RotateCcw size={14} /> Review &amp; continue
          </button>
        </div>
      )}

      {draftsOpen && (
        <div onClick={() => setDraftsOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 300, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "6vh 24px" }}>
          <div onClick={(e) => e.stopPropagation()} className="card pad" style={{ width: "min(620px,100%)", background: "var(--card)", maxHeight: "82vh", overflowY: "auto" }} data-testid="drafts-panel">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Layers size={16} color="var(--gold-deep)" /> Saved drafts</h3>
              <button className="icon-btn" onClick={() => setDraftsOpen(false)}><X size={16} /></button>
            </div>
            {drafts.length === 0 ? (
              <p className="t-mut" style={{ fontSize: 13 }} data-testid="drafts-empty">No saved drafts yet. Anything you start capturing autosaves here.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {drafts.map((d) => (
                  <div key={d.id} className="card pad" data-testid={`draft-row-${d.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 12, background: d.id === currentDraftId ? "var(--card-2)" : "var(--card)" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t-strong" style={{ fontSize: 13 }}>{d.label}{d.id === currentDraftId && <span className="tag-soft" style={{ marginLeft: 8 }}>current</span>}</div>
                      <div className="t-mut" style={{ fontSize: 11.5 }}>{d.item_count} line(s){d.customer_name ? ` · ${d.customer_name}` : ""} · saved {d.updated_at ? new Date(d.updated_at).toLocaleString("en-ZA") : "—"}</div>
                    </div>
                    <button className="btn sm gold" onClick={() => resumeDraft(d.id)} data-testid={`draft-resume-${d.id}`}><RotateCcw size={13} /> Resume</button>
                    <button className="icon-btn" style={{ color: "var(--red)" }} title="Delete draft" onClick={() => setConfirmDelDraft(d)} data-testid={`draft-delete-${d.id}`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ borderTop: "1px solid var(--line)", marginTop: 14, paddingTop: 12, display: "flex", justifyContent: "flex-end" }}>
              <button className="btn ghost" onClick={startNewDraft} data-testid="start-new-draft-btn"><FilePlus2 size={14} /> Start a new blank capture</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelDraft && (
        <div onClick={() => setConfirmDelDraft(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 320, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} className="card pad" style={{ width: "min(420px,100%)", background: "var(--card)" }} data-testid="draft-delete-modal">
            <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}><Trash2 size={16} color="var(--red)" /> Delete draft?</h3>
            <p style={{ fontSize: 13, color: "var(--text-2)" }}>This permanently removes the draft <b>{confirmDelDraft.label}</b>. No job has been created yet.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button className="btn ghost" onClick={() => setConfirmDelDraft(null)} data-testid="draft-delete-cancel">Cancel</button>
              <button className="btn" style={{ background: "var(--red)", color: "#fff" }} onClick={() => deleteDraft(confirmDelDraft)} data-testid="draft-delete-confirm">Delete draft</button>
            </div>
          </div>
        </div>
      )}

      {allDraftsOpen && (
        <div onClick={() => setAllDraftsOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 300, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "6vh 24px" }}>
          <div onClick={(e) => e.stopPropagation()} className="card pad" style={{ width: "min(720px,100%)", background: "var(--card)", maxHeight: "82vh", overflowY: "auto" }} data-testid="all-drafts-panel">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Layers size={16} color="var(--gold-deep)" /> Every staff member's saved drafts</h3>
              <button className="icon-btn" onClick={() => setAllDraftsOpen(false)}><X size={16} /></button>
            </div>
            <p className="t-mut" style={{ fontSize: 12, marginTop: 0 }}>
              Anything a technician or receptionist has started capturing but not yet submitted as a job —
              useful when a job goes out with fewer lines than expected (the rest may still be sitting here).
            </p>
            <div className="search" style={{ maxWidth: 320, marginBottom: 10 }}>
              <Search size={14} />
              <input placeholder="Search by DN, customer or staff name…" value={allDraftsQ}
                onChange={(e) => {
                  const v = e.target.value;
                  setAllDraftsQ(v);
                  if (allDraftsSearchTimer.current) clearTimeout(allDraftsSearchTimer.current);
                  allDraftsSearchTimer.current = setTimeout(() => loadAllDrafts(v), 300);
                }}
                data-testid="all-drafts-search" />
            </div>
            {allDraftsBusy ? (
              <p className="t-mut" style={{ fontSize: 13 }}>Loading…</p>
            ) : allDrafts.length === 0 ? (
              <p className="t-mut" style={{ fontSize: 13 }} data-testid="all-drafts-empty">No saved drafts found.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {allDrafts.map((d) => (
                  <div key={d.id} className="card pad" data-testid={`all-draft-row-${d.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t-strong" style={{ fontSize: 13 }}>{d.label}</div>
                      <div className="t-mut" style={{ fontSize: 11.5 }}>
                        {d.item_count} line(s){d.customer_name ? ` · ${d.customer_name}` : ""} · by <b>{d.user_name}</b> · saved {d.updated_at ? new Date(d.updated_at).toLocaleString("en-ZA") : "—"}
                      </div>
                    </div>
                    <button className="btn sm ghost" onClick={() => viewAllDraft(d)} data-testid={`all-draft-view-${d.id}`}>View lines</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {viewingDraft && (
        <div onClick={() => setViewingDraft(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 340, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} className="card pad" style={{ width: "min(640px,100%)", background: "var(--card)", maxHeight: "82vh", overflowY: "auto" }} data-testid="all-draft-detail-modal">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <h3 style={{ margin: 0 }}>{viewingDraft.summary.label}</h3>
              <button className="icon-btn" onClick={() => setViewingDraft(null)}><X size={16} /></button>
            </div>
            <p className="t-mut" style={{ fontSize: 12 }}>
              Captured by <b>{viewingDraft.summary.user_name}</b> · last saved {viewingDraft.summary.updated_at ? new Date(viewingDraft.summary.updated_at).toLocaleString("en-ZA") : "—"} · not yet submitted as a job
            </p>
            <div className="tbl-wrap">
              <table><thead><tr><th>#</th><th>Description</th><th>Serial / issue</th><th>Discipline</th><th>Qty</th></tr></thead>
                <tbody>
                  {(viewingDraft.payload.items || []).filter((it) => (it.description || "").trim()).map((it, i) => (
                    <tr key={it._uid || i} data-testid={`all-draft-detail-line-${i}`}>
                      <td className="t-mut">{i + 1}</td>
                      <td className="t-strong">{it.description || "—"}</td>
                      <td className="mono t-mut">{it.serial_number || it.issue_number || "—"}</td>
                      <td>{it.discipline || "—"}</td>
                      <td>{it.quantity || 1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


      {created ? (
        <div className="card pad" data-testid="job-created">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div className="mi" style={{ background: "var(--green-bg)", color: "var(--green)", width: 38, height: 38, borderRadius: 11, display: "grid", placeItems: "center" }}><CheckCircle2 size={20} /></div>
            <div><div className="head" style={{ fontSize: 17, fontWeight: 700 }}>Job created</div><div style={{ fontSize: 12.5, color: "var(--text-2)" }}>{created.items.length} item(s) · {created.customer_name}</div></div>
          </div>
          <div className="tbl-wrap" style={{ marginBottom: 16 }}>
            <table><thead><tr><th>Equipment</th><th>Serial / issue</th><th>Certificate no.</th></tr></thead>
              <tbody>{created.items.map((it) => <tr key={it.id}><td className="t-strong">{it.description}</td><td className="mono t-mut">{it.serial_number || it.issue_number || "—"}</td><td className="mono t-strong" data-testid={`created-item-cert-${it.id}`}>{it.reserved_certificate_number || it.certificate_number || "—"}</td></tr>)}</tbody>
            </table>
          </div>
          {created.certificate_address && (
            <div className="card pad" data-testid="created-cert-address"
              style={{ marginBottom: 16, borderLeft: `3px solid ${created.certificate_address.source === "end_user" ? "var(--gold-deep)" : "var(--line)"}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <MapPin size={14} color="var(--gold-deep)" />
                <span className="t-strong" style={{ fontSize: 12.5 }}>Certificate address</span>
                <span className={`badge ${created.certificate_address.source === "end_user" ? "b-gold" : "b-slate"}`} style={{ fontSize: 10 }}>
                  <span className="bd" />{created.certificate_address.source === "end_user" ? "End user" : "Customer"}
                </span>
              </div>
              <div className="t-strong" style={{ fontSize: 13 }}>{created.certificate_address.name || "—"}</div>
              {created.certificate_address.address_unknown ? (
                <div className="t-mut" style={{ fontSize: 12, marginTop: 4 }}>Address not supplied{created.certificate_address.address_unknown_reason ? ` — ${created.certificate_address.address_unknown_reason}` : ""}</div>
              ) : (
                <div className="t-mut" style={{ fontSize: 12, marginTop: 2, whiteSpace: "pre-line" }}>
                  {[created.certificate_address.address_line_1, created.certificate_address.address_line_2,
                    [created.certificate_address.city, created.certificate_address.province].filter(Boolean).join(", "),
                    [created.certificate_address.country, created.certificate_address.postal_code].filter(Boolean).join(" · ")].filter(Boolean).join("\n") || "No address on record"}
                </div>
              )}
            </div>
          )}
          <div style={{ display: "flex", gap: 9 }}>
            <button className="btn dark" onClick={downloadReceivingPdf} disabled={pdfBusy} data-testid="download-receiving-pdf"><Printer size={15} /> {pdfBusy ? "Generating…" : "Download receiving PDF"}</button>
            <button className="btn gold" onClick={reset}><Plus size={15} /> New job</button>
          </div>
        </div>
      ) : (
        <div className="card pad">
          <Collapsible title="Job details" testid="rcv-job-details"
            summary={jobDetailsSummary} missing={jobDetailsMissing} defaultOpen>
          <div className="form-grid" style={{ marginBottom: 4 }}>
            <div className="fld" style={{ position: "relative" }}>
              <label>Customer <span className="req">*</span></label>
              {customer ? (
                <div className="input" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }} data-testid="selected-customer">
                  <span className="t-strong">
                    {customer.customer_number && <span className="mono" style={{ color: "var(--gold-deep)", marginRight: 8 }}>{customer.customer_number}</span>}
                    {customer.customer_name}
                  </span>
                  <button className="icon-btn" style={{ width: 24, height: 24 }} onClick={() => { setCustomer(null); setCustQ(""); }} data-testid="receiving-customer-clear"><X size={14} /></button>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }} className="input">
                    <Search size={14} color="var(--text-3)" />
                    <input style={{ border: "none", outline: "none", width: "100%", background: "none" }} placeholder="Search customer…" value={custQ} onChange={(e) => setCustQ(e.target.value)} data-testid="receiving-customer-search" />
                  </div>
                  {custResults.length > 0 && (
                    <div className="card" style={{ position: "absolute", zIndex: 10, top: 70, left: 0, right: 0, maxHeight: 360, overflowY: "auto" }}>
                      <div className="t-mut" style={{ fontSize: 11, padding: "6px 10px", borderBottom: "1px solid var(--line)" }}>{custResults.length} match{custResults.length !== 1 ? "es" : ""} — scroll to browse</div>
                      {custResults.map((c) => (
                        <button key={c.id} className="nav-item" style={{ color: "var(--text)", borderRadius: 0, gap: 8 }} onClick={() => { setCustomer(c); setCustResults([]); }} data-testid={`cust-opt-${c.id}`}>
                          {c.customer_number && <span className="mono" style={{ color: "var(--gold-deep)", fontSize: 11.5, minWidth: 60 }}>{c.customer_number}</span>}
                          <span>{c.customer_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="fld"><label>Delivery note no. <span className="req">*</span></label>
              <input className="input mono" value={dn} onChange={(e) => setDn(e.target.value)} placeholder="DN 2026-…" data-testid="receiving-dn" />
              {dnDupMatches.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, padding: "8px 10px", borderRadius: 6,
                  background: "var(--red-bg, #fde7e7)", color: "var(--red, #b3261e)" }} data-testid="receiving-dn-duplicate-warning">
                  <AlertTriangle size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                  <strong>This DN already exists</strong>
                  {dnDupMatches.map((m) => (
                    <div key={m.id} style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span>{m.customer_name || "Unknown customer"} · {m.item_count} item{m.item_count !== 1 ? "s" : ""} · received {m.date_received || "—"}</span>
                      <a href={`${window.location.origin}/app/active-lab?openJob=${m.id}`} target="_blank" rel="noopener noreferrer"
                        data-testid={`receiving-dn-duplicate-link-${m.id}`}
                        style={{ color: "inherit", textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: 3 }}>
                        Open in new tab <ExternalLink size={11} />
                      </a>
                    </div>
                  ))}
                </div>
              )}</div>
            <div className="fld"><label>Date received <span className="req">*</span></label><input className="input" type="date" value={dateReceived} onChange={(e) => setDateReceived(e.target.value)} /></div>
            <div className="fld"><label>Quote / estimate ref.</label><input className="input mono" value={quoteRef} onChange={(e) => setQuoteRef(e.target.value)} placeholder="RFQ-2026-…" /></div>
            <div className="fld"><label>Calibration type <span className="req">*</span></label>
              <select className="input" value={calType} onChange={(e) => setCalType(e.target.value)}><option value="sanas">SANAS Accredited</option><option value="traceable">Traceable</option></select></div>
            <div className="fld"><label>Received by <span className="req">*</span></label>
              <select className="input" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} required data-testid="receiving-received-by">
                <option value="">— select receiver —</option>
                {receivers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div className="fld" style={{ display: "flex", alignItems: "flex-end" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox" checked={endUser}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setEndUser(true);
                      setEndUserModalOpen(true);
                    } else {
                      setEndUser(false);
                    }
                  }}
                  data-testid="receiving-enduser" /> End User
              </label>
              {endUser && (
                <button className="btn ghost sm" type="button"
                  style={{ marginLeft: 10 }}
                  onClick={() => setEndUserModalOpen(true)}
                  data-testid="receiving-enduser-edit-inline">
                  <Edit3 size={12} /> Edit
                </button>
              )}
            </div>
          </div>

          {customer && lastIssueNumbers.length > 0 && (
            <div className="card pad" data-testid="last-issue-numbers-box"
              style={{ marginTop: 10, background: "var(--card-2)", borderLeft: "3px solid var(--gold-deep)" }}>
              <div className="t-strong" style={{ fontSize: 12.5, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <Info size={13} color="var(--gold-deep)" /> Last {lastIssueNumbers.length} Issue Number{lastIssueNumbers.length !== 1 ? "s" : ""} used for {customer.customer_name}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {lastIssueNumbers.map((r, idx) => (
                  <div key={idx} data-testid={`last-issue-number-row-${idx}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, flexWrap: "wrap" }}>
                    <span className="mono t-strong" style={{ color: "var(--gold-deep)" }}>{r.issue_number}</span>
                    <span className="t-mut">·</span>
                    <span>{r.description || "—"}</span>
                    <span className="t-mut">·</span>
                    <span className="t-mut">
                      {r.completed ? `Completed ${r.date ? new Date(r.date).toLocaleDateString("en-ZA") : ""}` : `Received ${r.date || "—"} (in progress)`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          </Collapsible>

          {/* Whole-job subcontractor routing */}
          <Collapsible title="Send to subcontractor" icon={Truck} testid="rcv-subcon"
            summary={subconSummary} missing={subconMissing}
            tone={jobToSubcon ? "accent" : "default"}
            defaultOpen={false}
            badge={jobToSubcon && <span className="badge b-gold" style={{ fontSize: 10 }}><span className="bd" />Whole job</span>}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13.5 }}>
              <input type="checkbox" checked={jobToSubcon}
                onChange={(e) => setJobToSubcon(e.target.checked)} data-testid="job-subcon-toggle" />
              <Truck size={15} color="var(--gold-deep)" /> <span className="t-strong">Send entire job to subcontractor</span>
            </label>
            {jobToSubcon && (
              <>
                <p className="t-mut" style={{ fontSize: 12, margin: "8px 0 10px" }}>
                  Every equipment line on this DN is routed to the selected subcontractor. Set a different subcontractor on an individual line only if you turn this off.
                </p>
                <div className="form-grid">
                  <div className="fld"><label>Subcontractor <span className="req">*</span></label>
                    <SubcontractorTypeahead
                      value={jobSubconName}
                      onChange={(v) => { setJobSubconName(v); setJobSubconId(""); }}
                      onPick={(sc) => { setJobSubconId(sc.id); setJobSubconName(sc.name); }}
                      activeOnly allowFreeText={false}
                      placeholder="Type to search existing subcontractors…"
                      testid="job-subcon-select" />
                  </div>
                  <div className="fld"><label>Sub Con reason <span className="req">*</span></label>
                    <select className="input" value={jobSubconReason}
                      onChange={(e) => setJobSubconReason(e.target.value)} data-testid="job-subcon-reason">
                      <option value="">— select reason —</option>
                      {SUBCON_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="fld"><label>Delivery method</label>
                    <select className="input" value={jobSubconDelivery}
                      onChange={(e) => setJobSubconDelivery(e.target.value)} data-testid="job-subcon-delivery">
                      <option value="">— if known —</option>
                      <option value="pte_deliver">PTE deliver</option>
                      <option value="courier">Courier</option>
                      <option value="subcontractor_collect">Subcontractor collect</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </>
            )}
          </Collapsible>

          {customer && (
            <Collapsible title="Customer details" testid="rcv-customer-details"
              summary={customerDetailsSummary}
              defaultOpen={false}
              tone="muted"
              badge={primaryContact && (
                <span className="badge b-gold" style={{ fontSize: 10 }} data-testid="primary-contact-badge">
                  <span className="bd" />Primary · {primaryContact.name || primaryContact.email}
                </span>
              )}>
              <div className="form-grid" style={{ fontSize: 12.5 }}>
                <div className="fld"><label>Contact person</label><div className="mono t-strong">{primaryContact?.name || customer.contact_person || "—"}</div></div>
                <div className="fld"><label>Contact number</label><div className="mono t-strong">{primaryContact?.phone || customer.phone || "—"}</div></div>
                <div className="fld"><label>Alt. number</label><div className="mono t-mut">{customer.alt_phone || "—"}</div></div>
                <div className="fld"><label>VAT number</label><div className="mono t-mut">{customer.vat_number || "—"}</div></div>
                <div className="fld"><label>Email</label><div className="mono t-mut">{primaryContact?.email || customer.email || "—"}</div></div>
                <div className="fld"><label>Address</label><div className="t-mut">{[customer.address_line_1, customer.address_line_2].filter(Boolean).join(", ") || "—"}</div></div>
                <div className="fld"><label>City / Province</label><div className="t-mut">{[customer.city, customer.province].filter(Boolean).join(", ") || "—"}</div></div>
                <div className="fld"><label>Country / Postal</label><div className="t-mut">{[customer.country, customer.postal_code].filter(Boolean).join(" · ") || "—"}</div></div>
              </div>
            </Collapsible>
          )}

          {customer && endUser && (
            <Collapsible title="End-user certificate address" icon={MapPin}
              testid="rcv-enduser"
              summary={endUserSummary}
              missing={endUserMissing}
              defaultOpen={false}
              tone="accent"
              actions={
                <button className="btn ghost sm" type="button"
                  onClick={() => setEndUserModalOpen(true)}
                  data-testid="rcv-enduser-edit">
                  <Edit3 size={13} /> Edit details
                </button>
              }>
              <p className="t-mut" style={{ fontSize: 12, margin: "0 0 10px" }}>
                Billing customer (<b>{customer.customer_name}</b>) remains the paying account. These details set the <b>name &amp; address printed on the certificate</b>.
              </p>
              <div className="form-grid" style={{ fontSize: 12.5 }}>
                <div className="fld"><label>Name</label><div className="t-strong">{endUserName || <span className="t-mut">—</span>}</div></div>
                <div className="fld"><label>Email</label><div className="mono t-mut">{endUserEmail || "—"}</div></div>
                <div className="fld"><label>Phone</label><div className="mono t-mut">{endUserPhone || "—"}</div></div>
                <div className="fld"><label>Company (cert name)</label><div className="t-strong">{endUserCompany || <span className="t-mut">—</span>}</div></div>
                {endUserAddrUnknown ? (
                  <div className="fld" style={{ gridColumn: "span 2" }}>
                    <label>Address</label>
                    <div className="t-mut">Not supplied{endUserAddrUnknownReason ? ` — ${endUserAddrUnknownReason}` : ""}</div>
                  </div>
                ) : (
                  <>
                    <div className="fld" style={{ gridColumn: "span 2" }}><label>Address</label><div className="t-mut">{[endUserAddr1, endUserAddr2].filter(Boolean).join(", ") || "—"}</div></div>
                    <div className="fld"><label>City / Province</label><div className="t-mut">{[endUserCity, endUserProvince].filter(Boolean).join(", ") || "—"}</div></div>
                    <div className="fld"><label>Country / Postal</label><div className="t-mut">{[endUserCountry, endUserPostal].filter(Boolean).join(" · ") || "—"}</div></div>
                  </>
                )}
              </div>
              {endUserContactId
                ? (() => {
                    const orig = contacts.find((c) => c.id === endUserContactId);
                    if (!orig) return null;
                    const changedEmail = (orig.email || "") !== (endUserEmail || "");
                    const changedPhone = (orig.phone || "") !== (endUserPhone || "");
                    if (!changedEmail && !changedPhone) return null;
                    return (
                      <div className="tag-soft" style={{ marginTop: 10, fontSize: 11 }} data-testid="enduser-update-hint">
                        ✎ On save, the customer record for &quot;{orig.name}&quot; will be updated
                      </div>
                    );
                  })()
                : (endUserName && (endUserEmail || endUserPhone)) && (
                  <div className="tag-soft" style={{ marginTop: 10, fontSize: 11 }} data-testid="enduser-create-hint">
                    ✓ On save, &quot;{endUserName}&quot; will be added to this customer&apos;s contacts
                  </div>
                )}
            </Collapsible>
          )}

          {customer && certAddr && !endUser && (
            <div className="card pad" data-testid="cert-address-preview"
              style={{ marginBottom: 12, background: "var(--card)", borderLeft: "3px solid var(--line)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <MapPin size={14} color="var(--gold-deep)" />
                <span className="t-strong" style={{ fontSize: 12.5 }}>Certificate will be issued to</span>
                <span className="badge b-slate" style={{ fontSize: 10 }} data-testid="cert-address-source">
                  <span className="bd" />Customer
                </span>
              </div>
              <div className="t-strong" style={{ fontSize: 13 }} data-testid="cert-address-name">{certAddr.name || "—"}</div>
              <div className="t-mut" style={{ fontSize: 12, marginTop: 2, whiteSpace: "pre-line" }}>{certAddr.lines.length ? certAddr.lines.join("\n") : "No address on record"}</div>
            </div>
          )}

          <Collapsible title="Equipment lines" testid="rcv-lines"
            summary={equipmentSummary} missing={equipmentMissing} defaultOpen
            badge={<span className="tag-soft" style={{ fontSize: 11 }}>{items.length}</span>}>
          <div className="notice" style={{ marginBottom: 18 }}><Info size={16} /><span>Enter <b>Part number</b> or <b>Serial number</b> on a line and click the lookup icon — if the item has been calibrated before for this customer we&apos;ll pre-fill the rest and link the history.</span></div>

          {items.map((it, i) => (
            <ReceivingLineItem key={it._uid || i}
              it={it} i={i} total={items.length}
              customer={customer}
              techs={techs}
              subcontractors={subcontractors}
              jobToSubcon={jobToSubcon}
              jobSubconName={jobSubconName}
              jobSubconReason={jobSubconReason}
              setItem={setItem}
              removeItem={removeItem}
              applyMatch={applyMatch}
              onOpenHistory={setHistoryOpen}
            />
          ))}
          <button className="btn ghost" style={{ width: "100%", justifyContent: "center", borderStyle: "dashed" }} onClick={addItem} data-testid="add-line-btn"><Plus size={15} /> Add another equipment line</button>
          </Collapsible>

          <div className="fld" style={{ marginTop: 14 }}>
            <label>Admin notes (visible to staff only)</label>
            <textarea className="input" rows={2} value={notesAdmin} onChange={(e) => setNotesAdmin(e.target.value)}
              placeholder="Anything admin/dispatch needs to know — not shown to the customer"
              data-testid="receiving-notes-admin" />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 9, marginTop: 18 }}>
            <span className="t-mut" style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="draft-saved-indicator">
              {draftSavedAt
                ? <><Save size={13} color="var(--green)" /> Draft saved automatically · {new Date(draftSavedAt).toLocaleTimeString("en-ZA")}</>
                : <><Clock size={13} /> Draft autosaves as you type</>}
            </span>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              {allComplete ? (
                <span data-testid="rcv-ready-to-submit"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600,
                           color: "var(--green, #1f8a4d)", background: "var(--green-bg, #e7f5ed)",
                           padding: "5px 10px", borderRadius: 999, border: "1px solid var(--green, #1f8a4d)" }}>
                  <CheckCircle2 size={13} /> All sections complete — ready to submit
                </span>
              ) : (
                <span data-testid="rcv-blocking-missing" className="t-mut"
                  style={{ fontSize: 11.5, maxWidth: 360, textAlign: "right" }}
                  title={blockingMissing.join("\n")}>
                  {blockingMissing.length} field{blockingMissing.length === 1 ? "" : "s"} still needed
                </span>
              )}
              <button className="btn gold" onClick={create} disabled={busy || !allComplete} data-testid="create-job-btn"><CheckCircle2 size={15} /> {busy ? "Creating…" : "Create job & assign"}</button>
            </div>
          </div>
        </div>
      )}

      {historyOpen && <EquipmentModal id={historyOpen} canEdit={false} onClose={() => setHistoryOpen(null)} onChanged={() => {}} />}
      <EndUserModal
        open={endUserModalOpen}
        onClose={() => setEndUserModalOpen(false)}
        customer={customer}
        contacts={contacts}
        initial={{
          name: endUserName, email: endUserEmail, phone: endUserPhone,
          contactId: endUserContactId,
          company: endUserCompany, addr1: endUserAddr1, addr2: endUserAddr2,
          city: endUserCity, province: endUserProvince, country: endUserCountry, postal: endUserPostal,
          addrUnknown: endUserAddrUnknown, addrUnknownReason: endUserAddrUnknownReason,
        }}
        onSave={(v) => {
          setEndUser(true);
          setEndUserName(v.name); setEndUserEmail(v.email); setEndUserPhone(v.phone);
          setEndUserContactId(v.contactId);
          setEndUserCompany(v.company); setEndUserAddr1(v.addr1); setEndUserAddr2(v.addr2);
          setEndUserCity(v.city); setEndUserProvince(v.province); setEndUserCountry(v.country); setEndUserPostal(v.postal);
          setEndUserAddrUnknown(v.addrUnknown); setEndUserAddrUnknownReason(v.addrUnknownReason);
        }}
        onCancelTickback={() => setEndUser(false)}
      />
    </>
  );
}
